/**
 * Status-bar plant dots (SPEC §17.2, launch blocker CB3): the gateway reports every Rithmic plant
 * of the session's login in `SessionState.plants` ("ticker", "order", "history", "pnl"), each
 * with a state and, when it is not up, why. This maps them to the T / O / H / P dots and their
 * tooltips. Pure: structural types only, no protobuf runtime, so it runs under `node --test`.
 */

/** `helm.v1.PlantState` values (common.proto). */
export const PLANT_STATE = {
  UNSPECIFIED: 0,
  DOWN: 1,
  DEGRADED: 2,
  UP: 3,
  RECONNECTING: 4,
} as const;

/** Structural subset of `helm.v1.PlantStatus`. */
export type PlantStatusLike = { plant: string; state: number; detail?: string };

/** Dot colour: green, amber, red, grey. */
export type PlantTone = "up" | "warn" | "down" | "idle";

export type PlantDot = {
  /** Status-bar letter. */
  key: "T" | "O" | "H" | "P";
  /** `PlantStatus.plant`. */
  plant: string;
  name: string;
  tone: PlantTone;
  /** Short state word for the tooltip / screen readers. */
  label: string;
  /** Tooltip: "Ticker plant: reconnecting — dropped: no data for 2 s". */
  title: string;
};

const DOTS: readonly { key: PlantDot["key"]; plant: string; name: string }[] = [
  { key: "T", plant: "ticker", name: "Ticker" },
  { key: "O", plant: "order", name: "Order" },
  { key: "H", plant: "history", name: "History" },
  { key: "P", plant: "pnl", name: "PnL" },
];

function toneOf(state: number): { tone: PlantTone; label: string } {
  switch (state) {
    case PLANT_STATE.UP:
      return { tone: "up", label: "up" };
    case PLANT_STATE.RECONNECTING:
      return { tone: "warn", label: "reconnecting" };
    case PLANT_STATE.DEGRADED:
      return { tone: "warn", label: "degraded" };
    case PLANT_STATE.DOWN:
      return { tone: "down", label: "down" };
    default:
      return { tone: "idle", label: "idle" };
  }
}

/**
 * The four dots for `plants` (the latest `SessionState.plants`; `null` before any). A plant the
 * gateway did not report — an older gateway, or no Rithmic session yet — is "unknown" (grey),
 * never green. `connected` false (no trading stream) makes every dot unknown: the last state
 * heard may be stale.
 */
export function plantDots(plants: readonly PlantStatusLike[] | null, connected = true): PlantDot[] {
  return DOTS.map(({ key, plant, name }) => {
    const p = connected ? plants?.find((x) => x.plant === plant) : undefined;
    if (!p) {
      const why = connected ? "not reported yet" : "gateway stream disconnected";
      return { key, plant, name, tone: "idle", label: "unknown", title: `${name} plant: unknown (${why})` };
    }
    const { tone, label } = toneOf(p.state);
    const detail = p.detail?.trim() ?? "";
    return {
      key,
      plant,
      name,
      tone,
      label,
      title: detail.length > 0 ? `${name} plant: ${label} — ${detail}` : `${name} plant: ${label}`,
    };
  });
}

/** One line for the dots group's accessible name ("Rithmic plants: 3 of 4 up"). */
export function plantsSummary(dots: readonly PlantDot[]): string {
  const up = dots.filter((d) => d.tone === "up").length;
  return `Rithmic plants: ${String(up)} of ${String(dots.length)} up`;
}
